import { useEffect, useMemo } from 'react';
import { fetchMessages, sendMessage, sendMessageReply } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { createOutboxReconnectRunner } from '../screens/chat/outboxRunner';
import { useGlobalRealtime } from './GlobalRealtimeProvider';

/**
 * Автоповтор неотправленного при восстановлении связи (аудит 2026-10-17, UX-48). Живёт над
 * навигатором, а не в экране чата: повторить надо и то, что застряло в чате, из которого
 * человек давно ушёл, и то, что осталось с прошлого запуска.
 *
 * <p>Сам повтор грузится лениво (`import()`): основному веб-бандлу он не нужен до первого
 * подъёма связи, а запаса в бюджете бандла почти нет.
 */
export function OutboxAutoRetry(): null {
  const { wsConnected } = useGlobalRealtime();
  const { user } = useAuth();
  const ownerId = user?.userId ?? null;
  // Свой бегунок на каждый аккаунт: сменили аккаунт — его очередь повторяется при первой связи.
  const runner = useMemo(
    () =>
      createOutboxReconnectRunner(async () => {
        if (!ownerId) return;
        const { retryOutbox } = await import('../screens/chat/chatOutboxRetry');
        await retryOutbox({
          ownerId,
          send: (item) =>
            item.replyToMessageId
              ? sendMessageReply(item.chatId, item.ciphertext, item.replyToMessageId)
              : sendMessage(item.chatId, item.ciphertext),
          fetchSince: (chatId, sinceIso) => fetchMessages(chatId, { after: sinceIso, limit: 80 }),
        });
      }),
    [ownerId],
  );
  useEffect(() => {
    runner.update(wsConnected);
  }, [runner, wsConnected]);
  return null;
}
